import { toValue } from '@vueuse/core';
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useMutation } from '@/helpers/composables/useApollo';

import { Platform, SessionEnvironmentContextInput, SraEventAction } from '@/@types/graphql-types';
import { ApplicationContext as applicationContext, TrackingAppId as appId } from '@/helpers/tracking/tracking-helper';
import { getUtmSearchParams } from '@/helpers/composables/useArbitrage';

import {
	TrackSraEventDocument,
	TrackSraEventMutation,
	TrackSraEventMutationVariables,
} from '@/features/sponsored-recommendations/graphql/mutations/TrackSraEvent.mutation';
import type { MaybeRefOrGetter } from '@vueuse/core';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { storeToRefs } from 'pinia';
import { SessionEnvironment, useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { getSrFromBidId, trimForTracking } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';

interface TrackSraArguments {
	action: SraEventAction;
	sponsoredAd: MaybeRefOrGetter<SponsoredAdFragment | undefined>;
	jwEntityID?: string;
	position?: number;
	progress?: number;
	isSrTampered?: boolean;
	externalTrackerURL?: string;
	externalTrackerStatus?: number;
	externalTrackerURLExpanded?: string;
	isSeo?: boolean;
	buyBoxType?: NewBuyBoxView;
}

export type TrackSraEvent = ReturnType<typeof useBackendTracking>['trackSraEvent'];

const DOMAIN = `https://www.${JW_CONFIG.DOMAIN}` as const;

export function useBackendTracking() {
	const route = useRoute();
	const trackingStore = useTrackingStore();
	const { currentPageType } = storeToRefs(useRoutingStore());
	const { country, language } = storeToRefs(useLanguageStore());

	const pageUrl = computed(() => {
		const params = getUtmSearchParams();
		const url = new URL(route.fullPath, DOMAIN);

		if (params) params.forEach((value, key) => url.searchParams.set(key, value));

		return url.href;
	});

	const sessionEnvironmentContext = computed<SessionEnvironmentContextInput>(() => {
		const {
			// fields not in mutation
			sessionReady,
			originRefrSocial,

			// fields to trim
			origin,
			originPageUrl,
			originPageReferred,

			// fields to rename
			hasAdBlocker: hasAdblocker,

			...sessionEnvironment
		} = trackingStore.sessionEnvironment as SessionEnvironment;

		void sessionReady;
		void originRefrSocial;

		return {
			...sessionEnvironment,
			hasAdblocker,
			origin: trimForTracking(origin),
			originPageURL: trimForTracking(originPageUrl),
			originPageReferred: trimForTracking(originPageReferred),
		};
	});

	function trackSraEvent({
		action,
		sponsoredAd,
		jwEntityID,
		position,
		progress,
		isSrTampered,
		externalTrackerURL,
		externalTrackerStatus,
		externalTrackerURLExpanded,
		buyBoxType,
	}: TrackSraArguments) {
		const bidId = toValue(sponsoredAd)?.bidId;
		// if seo is true, send shadow true because we send it on the server side
		const isShadowEvent = toValue(sponsoredAd)?.campaign == null;
		const campaignName = toValue(sponsoredAd)?.campaign?.name;
		const sr = getSrFromBidId(bidId);

		const debuggingInfo = computed(() => {
			return {
				action,
				campaignName,
				sponsoredAd: JSON.stringify(toValue(sponsoredAd)),
				pageType: currentPageType.value,
				jwEntityID,
			};
		});

		if (!bidId) {
			captureMessageForSentry(
				'[Bid ID is empty]:',
				{
					where: 'Composable: useBackendTracking',
					mutationInfo: debuggingInfo,
				},
				'error',
				{ SR: campaignName ?? '' }
			);
		}

		if (currentPageType.value === null) {
			captureMessageForSentry(
				'[Page Type is empty]:',
				{
					where: 'Composable: useBackendTracking',
					mutationInfo: {
						action,
						currentPageType: currentPageType.value,
					},
				},
				'error',
				{ SR: campaignName ?? '' }
			);
		}

		const { mutate, onError } = useMutation<TrackSraEventMutation, TrackSraEventMutationVariables>(
			TrackSraEventDocument
		);

		if (bidId) {
			mutate({
				input: {
					bidId,
					action,
					sraInput: {
						appId,
						applicationContext: {
							...applicationContext,
							...(buyBoxType && { extras: JSON.stringify({ buyboxType: buyBoxType }) }),
						},
						country: country.value,
						language: language.value,
						pageType: currentPageType.value,
						placement: sr.placement ?? undefined,
						platform: Platform.Web,
						sessionEnvironmentContext: sessionEnvironmentContext.value,
					},
					jwEntityID,
					position,
					progress,
					pageUrl: pageUrl.value,
					isShadowEvent,
					isSrTampered,
					externalTrackerURL,
					externalTrackerStatus,
					externalTrackerURLExpanded,
				},
			});
		}

		onError(error =>
			captureMessageForSentry(
				'[GraphQL Track SRA Event]:',
				{
					error,
					where: 'Composable: useBackendTracking',
					mutationInfo: {
						...debuggingInfo,
						position,
						progress,
					},
				},
				'error',
				{ SR: campaignName ?? '' }
			)
		);
	}
	return { trackSraEvent };
}
